import { AfterViewInit, Component, CUSTOM_ELEMENTS_SCHEMA, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';

import { UserFilterForm } from '../../../form-model/user-filter.form.model';
import { UserAdvancedFiltersComponent } from '../user-advanced-filters/user-advanced-filters.component';
import { TagWithOverflowComponent } from '../../../shared-components/tag-with-overflow/tag-with-overflow.component';

import '@maersk-global/mds-components-core/mc-button';
import '@maersk-global/mds-components-core/mc-tag';
import '@maersk-global/mds-components-core/mc-modal';
import '@maersk-global/mds-components-core/mc-tooltip';
import { UserSearchTextForm } from '../../../form-model/user-search-text.form.model';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ToastrService } from '../../../services/toastr-service';
import { UserManagementService } from '../../../services/user-management.service';
import { downloadBlob, handleDownloadError, generateDateFilename } from '../../../helper/download-helpers';
import { DownloadFilterCriteria, toFilter, toEqualsFilter, FilterOperator } from '../../../models/download-filter.model';

type FilterValue = string | string[] | boolean[];

@Component({
  selector: 'app-user-filters',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, RouterModule, UserAdvancedFiltersComponent, TagWithOverflowComponent],
  templateUrl: './user-filters.component.html',
  styleUrl: './user-filters.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class UserFiltersComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input({ required: true }) filterFormGroup!: FormGroup<{
    userSearchTextForm: FormGroup<UserSearchTextForm>;
    userFilterForm: FormGroup<UserFilterForm>;
  }>;
  @Input() activeTab!: number;

  @ViewChild(UserAdvancedFiltersComponent)
  advanceFilterComponent!: UserAdvancedFiltersComponent;

  showAdvanceFilters = false;
  taggedFilters: { key: string; value: FilterValue }[] = [];
  savedFilter!: FormGroup<UserFilterForm>;
  isDownload = false;
  //readonly enableUserDownload = environment.Features.EnableUserDownload;
  private readonly destroyed$ = new Subject<void>();

  constructor(
    private readonly fb: FormBuilder,
    private readonly router: Router,
    private readonly toastrService: ToastrService,
    private readonly userManagementService: UserManagementService,
  ) {}

  navigateToCreate(): void {
    this.router.navigate(['/users/create']);
  }

  // ---------------- INIT ----------------

  ngAfterViewInit(): void {
    this.subscribeToFormChanges();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['activeTab'] && !changes['activeTab'].firstChange) {
      this.taggedFilters = [];
    }
  }

  // ---------------- SUBSCRIPTIONS ----------------

  private subscribeToFormChanges() {
    const form = this.filterFormGroup.controls.userFilterForm;

    form.controls.applyUserFilter.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe((value) => {
      if (value) {
        this.showAdvanceFilters = false;
        this.updateSelectedFiltersSection();
      }
    });

    // Only act on true (search applied), not on false (grid resets the flag back after processing).
    this.filterFormGroup.controls.userSearchTextForm.controls.appliedUserSearchTextFilter?.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((value) => {
        if (value) {
          this.updateSelectedFiltersSection();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  // ---------------- MODAL HANDLING ----------------

  showAdvanceFiltersModal() {
    this.showAdvanceFilters = true;
    this.saveCurrentFilters();
  }

  onDownload(): void {
    if (this.isDownload) return;
    this.isDownload = true;
    const filters = this.buildDownloadFilters();
    const prefix = this.activeTab === 0 ? 'shop-users' : 'other-users';

    this.userManagementService.downloadToExcel(this.activeTab === 0, { columns: [], filters }).subscribe({
      next: (blob: Blob) => {
        downloadBlob(blob, generateDateFilename(prefix));
        this.isDownload = false;
      },
      error: (err) => {
        handleDownloadError(err, this.toastrService, 'Failed to download User List. Please try again later.');
        this.isDownload = false;
      },
    });
  }

  private buildDownloadFilters(): DownloadFilterCriteria[] {
    const filters: DownloadFilterCriteria[] = [];
    const form = this.filterFormGroup.controls.userFilterForm;

    const userName = form.controls.userName.value;
    if (userName) filters.push(toEqualsFilter('login_name', userName));

    const email = form.controls.email.value;
    if (email) filters.push(toEqualsFilter('email_address', email));

    const fullName = form.controls.fullName.value;
    if (fullName) filters.push(toEqualsFilter('full_name', fullName));

    const roles = form.controls.role.value;
    if (roles && roles.length > 0) filters.push(toFilter('role', roles));

    const regions = form.controls.region.value;
    if (regions && regions.length > 0) {
      filters.push({ column: 'region_code', operator: FilterOperator.In, value: regions.join(',') });
    }

    const clusters = form.controls.cluster.value;
    if (clusters && clusters.length > 0) filters.push(toFilter('cluster_code', clusters));

    const shopCodes = form.controls.shopCode.value;
    if (shopCodes && shopCodes.length > 0) filters.push(toFilter('shop_code', shopCodes));

    const isActiveValues = form.controls.isActive.value;
    if (isActiveValues?.length === 1) {
      filters.push(toEqualsFilter('is_active', String(isActiveValues[0])));
    }

    return filters;
  }

  closeAdvanceFiltersModal() {
    if (this.showAdvanceFilters) {
      this.showAdvanceFilters = false;
      this.filterFormGroup.controls.userFilterForm.reset(this.savedFilter.value);
    }
    this.updateSelectedFiltersSection();
  }

  // ---------------- SAVE STATE ----------------

  private saveCurrentFilters() {
    const f = this.filterFormGroup.controls.userFilterForm.controls;

    this.savedFilter = this.fb.group({
      userName: this.fb.control(f.userName.value),
      email: this.fb.control(f.email.value),
      fullName: this.fb.control(f.fullName.value),
      role: this.fb.control(f.role.value),
      region: this.fb.control(f.region.value),
      cluster: this.fb.control(f.cluster.value),
      shopCode: this.fb.control(f.shopCode.value),
      isActive: this.fb.control(f.isActive.value),
      applyUserFilter: this.fb.control(f.applyUserFilter.value),
    });
  }

  // ---------------- TAGGING ----------------

  updateSelectedFiltersSection() {
    const form = this.filterFormGroup.controls.userFilterForm.controls;

    this.markAsTagged('User Name', form.userName.value);
    this.markAsTagged('Email', form.email.value);
    this.markAsTagged('Full Name', form.fullName.value);

    const roleCodes = form.role.value ?? [];
    const roleLabels = roleCodes.map(
      (code: string) => this.advanceFilterComponent?.rolesList?.find((r: any) => r.value === code)?.label ?? code,
    );
    this.markAsTagged('Role', roleLabels.length ? roleLabels : null);

    const regionCodes = form.region.value ?? [];
    const regionLabels = regionCodes.map(
      (code: string) => this.advanceFilterComponent?.regionList?.find((r: any) => r.value === code)?.label ?? code,
    );
    this.markAsTagged('Region', regionLabels.length ? regionLabels : null);

    const clusterCodes = form.cluster.value ?? [];
    const clusterLabels = clusterCodes.map(
      (code: string) => this.advanceFilterComponent?.clusterList?.find((c: any) => c.value === code)?.label ?? code,
    );
    this.markAsTagged('Cluster', clusterLabels.length ? clusterLabels : null);

    this.markAsTagged('Shop Code', form.shopCode.value?.length ? form.shopCode.value : null);
    const statusLabels = (form.isActive.value as boolean[])?.map((v) => (v ? 'Active' : 'Inactive')) ?? null;
    this.markAsTagged('Status', statusLabels?.length ? statusLabels : null);
  }

  markAsTagged(key: string, value: FilterValue | null) {
    const currentValue = this.getFilterValue(key);

    if (currentValue !== value) {
      this.removeFilter(key);

      if (value !== null && value !== '' && (!Array.isArray(value) || value.length > 0)) {
        this.taggedFilters.unshift({ key, value });
      }
    }
  }

  removeFilter(key: string) {
    this.taggedFilters = this.taggedFilters.filter((f) => f.key !== key);
  }

  getFilterValue(key: string) {
    const filter = this.taggedFilters.find((f) => f.key === key);
    return filter ? filter.value : null;
  }

  // ---------------- CLEAR ----------------

  clearAllSelectedFilters() {
    this.filterFormGroup.controls.userFilterForm.reset();
    this.filterFormGroup.controls.userSearchTextForm.reset();
    this.filterFormGroup.controls.userFilterForm.controls.applyUserFilter.setValue(true);
    this.updateSelectedFiltersSection();
  }

  // ---------------- REMOVE TAG (FIXED) ----------------

  unselectSpecificFilter(tag: any, value?: any) {
    const form = this.filterFormGroup.controls.userFilterForm.controls;

    switch (tag.key) {
      case 'User Name':
        form.userName.setValue(null);
        break;

      case 'Email':
        form.email.setValue(null);
        break;

      case 'Full Name':
        form.fullName.setValue(null);
        break;

      case 'Role': {
        const code = this.advanceFilterComponent?.rolesList?.find((r: any) => r.label === value)?.value;
        form.role.setValue((form.role.value || []).filter((v) => v !== (code ?? value)));
        break;
      }

      case 'Region': {
        const code = this.advanceFilterComponent?.regionList?.find((r: any) => r.label === value)?.value;
        form.region.setValue((form.region.value || []).filter((v) => v !== (code ?? value)));
        break;
      }

      case 'Cluster': {
        const code = this.advanceFilterComponent?.clusterList?.find((c: any) => c.label === value)?.value;
        form.cluster.setValue((form.cluster.value || []).filter((v) => v !== (code ?? value)));
        break;
      }

      case 'Shop Code': {
        const remaining = (form.shopCode.value || []).filter((v: string) => v !== value);
        form.shopCode.setValue(remaining.length ? remaining : []);
        break;
      }

      case 'Status': {
        const boolValue = value === 'Active';
        form.isActive.setValue((form.isActive.value || []).filter((v: boolean) => v !== boolValue));
        break;
      }
    }

    form.applyUserFilter.setValue(true);
    this.updateSelectedFiltersSection();
  }

  // ---------------- HELPERS ----------------

  isArray(value: any): boolean {
    return Array.isArray(value);
  }

  asStringArray(value: FilterValue): string[] {
    return value as string[];
  }

  toStringArray(value: FilterValue): string[] {
    if (Array.isArray(value)) return value as string[];
    return [value];
  }

  getUnselectFn(tag: { key: string; value: FilterValue }): (item: string) => void {
    return (item: string) => this.unselectSpecificFilter(tag, item);
  }

  handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      (event.target as HTMLElement).click();
    }
  }
}
